import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/feature/PageMeta';
import { storeChannels } from '@/mocks/stores';
import { useScrollReveal } from '@/hooks/useScrollReveal';

function RevealWrap({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const { ref, isVisible } = useScrollReveal(0.06);
  return (
    <div ref={ref} className={`section-reveal ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

const categoryLabel: Record<string, string> = {
  reference: '書目查詢',
  physical: '實體與網路書店',
  digital: '電子書平台',
};

export default function Stores() {
  const referenceChannels = storeChannels.filter(c => c.category === 'reference');
  const physicalChannels = storeChannels.filter(c => c.category === 'physical');
  const digitalChannels = storeChannels.filter(c => c.category === 'digital');

  return (
    <>
      <PageMeta
        title="合作購書通路｜複刻文化 Fuko"
        description="複刻文化合作購書通路完整列表：博客來、誠品、金石堂、三民、momo、蝦皮、Amazon Kindle、TAAZE讀冊、Kobo、Pubu、OverDrive、MyBook、Hami Book。複刻文化為崧博出版合作經銷品牌之一。"
        canonical="https://fuko.sonbook.net/stores"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          '@id': 'https://fuko.sonbook.net/stores#collectionpage',
          url: 'https://fuko.sonbook.net/stores',
          name: '合作購書通路｜複刻文化 Fuko',
          isPartOf: { '@id': 'https://fuko.sonbook.net/#website' },
        }}
      />
      <Navbar />
      <main>

        {/* §1 · Hero */}
        <section className="relative w-full flex items-center justify-center paper-texture" style={{ minHeight: '100dvh', backgroundColor: 'oklch(var(--background-100))' }}>
          <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
            <img src="https://readdy.ai/api/search-image?query=A%20quiet%20refined%20independent%20bookstore%20interior%20with%20tall%20wooden%20shelves%20lining%20both%20sides%20of%20a%20narrow%20aisle%2C%20the%20shelves%20filled%20with%20a%20harmonious%20mix%20of%20Chinese%20thread-bound%20classic%20books%20with%20ivory%20title%20slips%20and%20European%20cloth-bound%20and%20leather-bound%20volumes%20in%20muted%20tones%20of%20olive%20burgundy%20brown%20and%20cream%2C%20soft%20natural%20light%20streaming%20through%20a%20tall%20arched%20window%20at%20the%20end%20of%20the%20aisle%20casting%20gentle%20shadows%20across%20the%20wooden%20floor%20and%20book%20spines%2C%20a%20warm%20reading%20lamp%20on%20a%20small%20antique%20side%20table%20illuminating%20a%20cozy%20corner%2C%20the%20atmosphere%20is%20peaceful%20contemplative%20and%20intellectual%20blending%20Eastern%20and%20Western%20bibliophile%20traditions%2C%20modern%20minimalist%20editorial%20photography%20style%20with%20warm%20amber%20and%20golden%20tones%2C%20sophisticated%20bookstore%20visual%20identity%2C%20refined%20restrained%20sophisticated%20mood%20without%20being%20commercial%20or%20busy%2C%20fine%20art%20documentary%20approach%2C%20clean%20composition%20with%20balanced%20negative%20space%2C%20editorial%20magazine%20quality&width=1600&height=900&seq=fuko-stores-hero-vintage-01&orientation=landscape&nocache=true" alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.50 }} />
            <span className="absolute top-[12%] right-[8%] font-heading font-bold select-none hidden xl:block" style={{ fontSize: 160, color: 'oklch(var(--foreground-950)/0.016)' }}>複刻</span>
            <span className="absolute bottom-[8%] left-[5%] font-display italic text-[10px] hidden lg:block" style={{ color: 'oklch(var(--foreground-400)/0.15)' }}>S.001</span>
          </div>

          <RevealWrap className="relative w-full">
            <div className="w-full px-6 md:px-12 lg:px-16 pt-20 md:pt-24 pb-20 md:pb-28" style={{ zIndex: 1 }}>
              <div className="max-w-[min(100%-32px,800px)] mx-auto text-center">
                <div className="flex items-center justify-center gap-3 mb-4" style={{ color: 'oklch(var(--foreground-400)/0.42)' }}>
                  <span className="font-display italic text-[13px]">S.001</span>
                  <span className="w-6 h-px" style={{ backgroundColor: 'oklch(var(--foreground-300)/0.20)' }} />
                  <span className="font-label text-[11px] tracking-[0.2em] uppercase">Stores</span>
                </div>

                <h1 className="font-heading font-bold text-foreground-900 leading-[1.15] mb-2" style={{ fontSize: 'clamp(2rem,4vw,3.5rem)', letterSpacing: '-0.01em' }}>
                  合作購書通路
                </h1>
                <p className="font-display italic font-bold mb-6" style={{ fontSize: 'clamp(0.9rem,1.3vw,1.1rem)', color: 'oklch(var(--primary-500))' }}>
                  Partner Bookstores
                </p>

                <p className="text-foreground-600 leading-[1.8] mb-3 max-w-lg mx-auto" style={{ fontSize: 'clamp(0.875rem,1.05vw,0.95rem)' }}>
                  複刻文化書籍可透過以下合作通路查詢與購買。
                </p>
                <p className="text-foreground-500 leading-[1.8] mb-8 max-w-lg mx-auto" style={{ fontSize: 'clamp(0.8rem,0.95vw,0.875rem)' }}>
                  價格、版本與庫存情形，請以各通路實際顯示為準。
                </p>

                <div className="flex items-center justify-center gap-4 mb-10">
                  <div className="h-px w-16" style={{ backgroundColor: 'oklch(var(--foreground-300)/0.15)' }} />
                </div>
              </div>
            </div>
          </RevealWrap>
        </section>

        <div className="ornament-divider my-10 md:my-14" aria-hidden="true">
          <div className="ornament-divider-icon font-display italic">&#10087;</div>
        </div>

        {/* §2 · Store Channels */}
        <section className="relative w-full flex items-center justify-center" style={{ minHeight: '100dvh', backgroundColor: 'oklch(var(--background-50))' }}>
          <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
            <span className="absolute top-[6%] right-[7%] font-display italic font-bold select-none hidden lg:block" style={{ fontSize: 100, color: 'oklch(var(--foreground-950)/0.011)' }}>Channels</span>
          </div>

          <RevealWrap className="relative w-full">
            <div className="w-full px-6 md:px-12 lg:px-16 py-20 md:py-28">
              <div className="max-w-[min(100%-32px,1000px)] mx-auto">
                <div className="mb-14 md:mb-20">
                  <div className="flex items-center gap-3 mb-3" style={{ color: 'oklch(var(--foreground-400)/0.38)' }}>
                    <span className="font-display italic text-[13px]">S.002</span>
                    <span className="w-6 h-px" style={{ backgroundColor: 'oklch(var(--foreground-300)/0.20)' }} />
                    <span className="font-label text-[11px] tracking-[0.15em] uppercase">Channels</span>
                  </div>
                  <h2 className="font-heading font-bold text-foreground-900 leading-[1.15]" style={{ fontSize: 'clamp(1.5rem,2.6vw,2.2rem)', letterSpacing: '-0.01em' }}>
                    現有合作通路
                  </h2>
                </div>

                {/* Category: Reference */}
                {referenceChannels.length > 0 && (
                  <div className="mb-14">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="font-label text-[10px] tracking-[0.25em] uppercase px-2 py-0.5 rounded" style={{ backgroundColor: 'oklch(var(--primary-100)/0.40)', color: 'oklch(var(--primary-600))' }}>
                        {categoryLabel.reference}
                      </span>
                    </div>
                    <div className="space-y-6">
                      {referenceChannels.map((channel) => (
                        <a
                          key={channel.name}
                          href={channel.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block group transition-colors duration-200"
                          style={{ borderBottom: '1px solid oklch(var(--foreground-200)/0.22)', paddingBottom: '28px' }}
                          onMouseEnter={e => {
                            const el = e.currentTarget as HTMLElement;
                            el.style.borderBottomColor = 'oklch(var(--primary-300)/0.40)';
                          }}
                          onMouseLeave={e => {
                            const el = e.currentTarget as HTMLElement;
                            el.style.borderBottomColor = 'oklch(var(--foreground-200)/0.22)';
                          }}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
                            <div>
                              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                                {channel.name}
                              </h3>
                              <p className="text-sm text-foreground-500 leading-relaxed max-w-lg">
                                {channel.description}
                              </p>
                            </div>
                            <span className="inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap flex-shrink-0" style={{ color: 'oklch(var(--primary-500))' }}>
                              前往 <i className="ri-external-link-line text-xs" />
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Category: Physical */}
                {physicalChannels.length > 0 && (
                  <div className="mb-14">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="font-label text-[10px] tracking-[0.25em] uppercase px-2 py-0.5 rounded" style={{ backgroundColor: 'oklch(var(--secondary-100)/0.50)', color: 'oklch(var(--secondary-600))' }}>
                        {categoryLabel.physical}
                      </span>
                    </div>
                    <div className="space-y-6">
                      {physicalChannels.map((channel) => (
                        <a
                          key={channel.name}
                          href={channel.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block group transition-colors duration-200"
                          style={{ borderBottom: '1px solid oklch(var(--foreground-200)/0.22)', paddingBottom: '28px' }}
                          onMouseEnter={e => {
                            const el = e.currentTarget as HTMLElement;
                            el.style.borderBottomColor = 'oklch(var(--secondary-300)/0.40)';
                          }}
                          onMouseLeave={e => {
                            const el = e.currentTarget as HTMLElement;
                            el.style.borderBottomColor = 'oklch(var(--foreground-200)/0.22)';
                          }}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
                            <div>
                              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-2 group-hover:text-secondary-600 transition-colors">
                                {channel.name}
                              </h3>
                              <p className="text-sm text-foreground-500 leading-relaxed max-w-lg">
                                {channel.description}
                              </p>
                            </div>
                            <span className="inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap flex-shrink-0" style={{ color: 'oklch(var(--secondary-500))' }}>
                              前往 <i className="ri-external-link-line text-xs" />
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Category: Digital */}
                {digitalChannels.length > 0 && (
                  <div className="mb-14">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="font-label text-[10px] tracking-[0.25em] uppercase px-2 py-0.5 rounded" style={{ backgroundColor: 'oklch(var(--accent-100)/0.40)', color: 'oklch(var(--accent-600))' }}>
                        {categoryLabel.digital}
                      </span>
                    </div>
                    <div className="space-y-6">
                      {digitalChannels.map((channel) => (
                        <a
                          key={channel.name}
                          href={channel.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block group transition-colors duration-200"
                          style={{ borderBottom: '1px solid oklch(var(--foreground-200)/0.22)', paddingBottom: '28px' }}
                          onMouseEnter={e => {
                            const el = e.currentTarget as HTMLElement;
                            el.style.borderBottomColor = 'oklch(var(--accent-300)/0.40)';
                          }}
                          onMouseLeave={e => {
                            const el = e.currentTarget as HTMLElement;
                            el.style.borderBottomColor = 'oklch(var(--foreground-200)/0.22)';
                          }}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
                            <div>
                              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-2 group-hover:text-accent-600 transition-colors">
                                {channel.name}
                              </h3>
                              <p className="text-sm text-foreground-500 leading-relaxed max-w-lg">
                                {channel.description}
                              </p>
                            </div>
                            <span className="inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap flex-shrink-0" style={{ color: 'oklch(var(--accent-500))' }}>
                              前往 <i className="ri-external-link-line text-xs" />
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Note */}
                <div className="mt-12 pt-8" style={{ borderTop: '2px solid oklch(var(--foreground-200)/0.18)' }}>
                  <p className="text-xs text-foreground-400 leading-relaxed">
                    複刻文化事業有限公司出版品於以上通路皆有上架。價格、版本與庫存情形，請以各通路實際顯示為準。若在通路找不到特定書籍，可前往
                    <a href="https://catalog.sonbook.net" target="_blank" rel="noopener noreferrer" className="font-medium btn-underline mx-1" style={{ color: 'oklch(var(--primary-500))' }}>
                      崧博出版書籍資料庫
                    </a>
                    查詢完整書目。
                  </p>
                </div>
              </div>
            </div>
          </RevealWrap>
        </section>

      </main>
      <Footer />
    </>
  );
}
